import { useState, useEffect, useCallback } from "react";
import {
  MagnifyingGlass, Target, Check, X, Prohibit, ArrowClockwise, Sparkle, Trash,
  ChatCircleText, UserPlus, Fire,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";

const STATUS_FILTERS = ["all", "new", "approved", "sent", "replied", "skipped", "suppressed", "converted"];
const TYPE_FILTERS = ["all", "seller", "buyer"];
const CLASS_LABELS = { inbound: "Inbound", distress: "Distress", cold: "Cold owner" };

function ScorePill({ score }) {
  const n = Number(score) || 0;
  const cls = n >= 75
    ? "border-brand/40 text-brand bg-brand/10"
    : n >= 55
      ? "border-white/20 text-white/70 bg-white/5"
      : "border-white/10 text-white/40 bg-white/5";
  return (
    <span className={`px-2 py-0.5 text-xs rounded-sm border ${cls}`} data-testid="prospect-score">
      {n}
    </span>
  );
}

export default function Prospects() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  // Default Email-ready — enrich (Tracerfy) is the path to actionable rows.
  const [actionableOnly, setActionableOnly] = useState(true);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [mining, setMining] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async () => {
    const params = { limit: 150, actionable: actionableOnly };
    if (status !== "all") params.status = status;
    if (type !== "all") params.prospect_type = type;
    try {
      const [{ data }, { data: s }, { data: a }] = await Promise.all([
        api.get("/prospects", { params }),
        api.get("/prospects/settings"),
        api.get("/prospects/analytics").catch(() => ({ data: null })),
      ]);
      setItems(Array.isArray(data) ? data : []);
      setSettings(s);
      setAnalytics(a);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load prospects");
    } finally {
      setLoading(false);
    }
  }, [status, type, actionableOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = items.filter((p) => {
    if (!q) return true;
    const hay = `${p.name || ""} ${p.owner_name || ""} ${p.address || ""} ${p.email || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const emailReadyCount = filtered.filter(
    (p) => p.status === "new" && p.email && Number(p.score) >= Number(settings?.min_score ?? 55),
  ).length;
  const approvedCount = items.filter((p) => p.status === "approved").length;

  const act = async (id, action) => {
    try {
      await api.post(`/prospects/${id}/${action}`);
      const msg = {
        approve: "Approved for send",
        skip: "Skipped",
        suppress: "Suppressed",
        replied: "Marked replied — sequence paused",
        unreplied: "Undone — sequence resumed",
      }[action] || "Done";
      if (action === "replied") {
        toast.success(msg, {
          action: { label: "Undo", onClick: () => act(id, "unreplied") },
          duration: 8000,
        });
      } else {
        toast.success(msg);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Could not ${action}`);
    }
  };

  const convertProspect = async (id) => {
    if (!window.confirm("Convert this prospect into a CRM lead?")) return;
    try {
      await api.post(`/prospects/${id}/convert`);
      toast.success("Converted to lead — now in your CRM pipeline");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not convert");
    }
  };

  const openDraft = async (p) => {
    setPreview(p);
    try {
      const { data } = await api.post(`/prospects/${p.id}/refresh-draft`);
      setPreview(data);
      setItems((prev) => prev.map((row) => (row.id === p.id ? { ...row, ...data } : row)));
    } catch (e) {
      // Fall back to stored draft if refresh fails
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not refresh draft");
    }
  };

  const removeOne = async (id) => {
    try {
      await api.delete(`/prospects/${id}`);
      toast.success("Removed from queue");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete");
    }
  };

  const purgeDenied = async () => {
    if (!window.confirm("Permanently remove all suppressed/skipped prospects from the list? Their emails stay blocked from future outreach.")) {
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/prospects/purge-denied");
      toast.success(`Cleared ${data?.deleted ?? 0} denied prospects`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Purge failed");
    } finally {
      setBusy(false);
    }
  };

  const bulkApprove = async (sendNow) => {
    const label = sendNow
      ? "Approve and try to send all email-ready prospects at/above min score?"
      : "Approve all email-ready prospects at/above min score? (cron/send will deliver within quiet hours)";
    if (!window.confirm(label)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/prospects/bulk-approve", null, {
        params: { send_now: sendNow, limit: 50 },
      });
      const sent = data?.send?.sent;
      toast.success(
        sent != null
          ? `Approved ${data.approved}; sent ${sent} now`
          : `Approved ${data?.approved ?? 0} email-ready (skipped ${data?.skipped ?? 0})`,
      );
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk approve failed");
    } finally {
      setBusy(false);
    }
  };

  const formatEnrichToast = (enrich) => {
    if (!enrich) return "";
    if (enrich.reason === "not_configured") {
      return "enriched: 0 (add TRACERFY_API_KEY on Render)";
    }
    if (enrich.reason === "monthly_cap") {
      return `enriched: 0 (monthly cap ${enrich.cap ?? "?"} reached)`;
    }
    const rem = enrich.remaining != null ? ` / cap remaining ${enrich.remaining}` : "";
    return `enriched: ${enrich.enriched || 0}${rem}`;
  };

  const runMine = async () => {
    setMining(true);
    try {
      const { data } = await api.post("/prospects/mine");
      const lienN = data?.taxlien?.upserted || 0;
      const plutoN = data?.pluto?.upserted || 0;
      const nysN = data?.nys_parcels?.upserted || 0;
      const acrisN = data?.acris?.upserted || 0;
      const expN = data?.expired?.upserted || 0;
      const siteN = data?.site_intent?.upserted || 0;
      const enrichN = data?.enrich?.enriched || 0;
      const errs = [data?.taxlien?.error, data?.pluto?.error, data?.nys_parcels?.error, data?.acris?.error, data?.expired?.error].filter(Boolean);
      if (errs.length) {
        toast.error(`Mine partial failure: ${errs.join("; ")}`);
      }
      const total = lienN + plutoN + nysN + acrisN + expN + siteN;
      toast.success(
        `Mined — tax liens: ${lienN}, ACRIS: ${acrisN}, NYC: ${plutoN}, suburbs: ${nysN}, expired: ${expN}, site: ${siteN}, ${formatEnrichToast(data?.enrich)}`,
      );
      if (total > 0 && actionableOnly && enrichN === 0 && siteN === 0 && data?.enrich?.reason === "not_configured") {
        setActionableOnly(false);
        toast.message("Showing all prospects — add TRACERFY_API_KEY to append owner emails (Email-ready was hiding no-email rows).");
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Mine failed");
    } finally {
      setMining(false);
    }
  };

  const sendNow = async (prospectId = null) => {
    const label = prospectId
      ? "Send this approved prospect now?"
      : `Send all approved / due outreach now? (daily cap still applies; quiet hours bypassed)`;
    if (!window.confirm(label)) return;
    setBusy(true);
    try {
      const params = prospectId ? { prospect_id: prospectId } : {};
      const { data } = await api.post("/prospects/send-now", null, { params });
      if (data?.reason === "email_not_configured") {
        toast.error("Email not configured (RESEND_API_KEY / SENDER_EMAIL)");
      } else if (data?.reason === "daily_cap") {
        toast.message("Daily send cap reached — raise Daily cap or wait until tomorrow");
      } else if (data?.reason === "quiet_hours") {
        toast.message("Quiet hours active (cron only) — try Send now again");
      } else {
        toast.success(`Sent ${data?.sent ?? 0}${data?.failed ? `, failed ${data.failed}` : ""}`);
      }
      await load();
      if (!prospectId) setStatus("sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const runEnrich = async () => {
    setEnriching(true);
    try {
      const { data } = await api.post("/prospects/enrich", null, { params: { limit: 20 } });
      if (data?.reason === "not_configured") {
        toast.error(data.hint || "Add TRACERFY_API_KEY on Render (see DEPLOY.md)");
      } else if (data?.reason === "monthly_cap") {
        toast.message(`Monthly enrich cap reached (${data.used}/${data.cap}). Raise SKIPTRACE_MONTHLY_CAP or wait until next month.`);
      } else {
        toast.success(`Enriched ${data?.enriched ?? 0} — cap remaining ${data?.remaining ?? "?"}`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Enrich failed");
    } finally {
      setEnriching(false);
    }
  };

  const saveSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const { data } = await api.patch("/prospects/settings", patch);
      setSettings((prev) => ({ ...prev, ...data }));
      toast.success("Outreach settings saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="prospects-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl text-white flex items-center gap-2">
            <Target size={28} weight="duotone" className="text-brand" /> Prospects
          </h1>
          <p className="text-white/50 text-sm mt-1">
            Intent-stacked outreach (inbound, ACRIS distress, public records) — email sequences, not MLS scrapes.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="prospects-purge-btn"
            onClick={purgeDenied}
            disabled={busy}
            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-sm border border-white/15 text-white/60 text-xs uppercase tracking-wider hover:text-white hover:border-white/30 disabled:opacity-60"
          >
            <Trash size={14} /> Clear denied
          </button>
          <button
            type="button"
            data-testid="prospects-bulk-approve-btn"
            onClick={() => bulkApprove(false)}
            disabled={busy || !settings?.email_configured}
            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-sm border border-brand/40 text-brand text-xs uppercase tracking-wider hover:bg-brand/10 disabled:opacity-60"
          >
            <Check size={14} /> Approve email-ready
            {emailReadyCount > 0 ? ` (${emailReadyCount})` : ""}
          </button>
          <button
            type="button"
            data-testid="prospects-bulk-send-btn"
            onClick={() => bulkApprove(true)}
            disabled={busy || !settings?.email_configured}
            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-sm border border-brand/40 text-brand text-xs uppercase tracking-wider hover:bg-brand/10 disabled:opacity-60"
          >
            Approve + send now
          </button>
          <button
            type="button"
            data-testid="prospects-send-now-btn"
            onClick={() => sendNow()}
            disabled={busy || !settings?.email_configured}
            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-sm bg-brand text-black text-xs uppercase tracking-wider font-medium hover:bg-brand-hover disabled:opacity-60"
          >
            Send approved now
            {status === "approved" && approvedCount > 0 ? ` (${approvedCount})` : ""}
          </button>
          <button
            type="button"
            data-testid="prospects-enrich-btn"
            onClick={runEnrich}
            disabled={enriching || mining || busy}
            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-sm border border-brand/40 text-brand text-xs uppercase tracking-wider hover:bg-brand/10 disabled:opacity-60"
          >
            <Sparkle size={14} className={enriching ? "animate-pulse" : ""} />
            {enriching ? "Enriching…" : "Enrich top prospects"}
          </button>
          <button
            type="button"
            data-testid="prospects-mine-btn"
            onClick={runMine}
            disabled={mining || busy || enriching}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-brand text-black text-xs uppercase tracking-wider font-medium hover:bg-brand-hover disabled:opacity-60"
          >
            <ArrowClockwise size={16} className={mining ? "animate-spin" : ""} />
            {mining ? "Mining…" : "Run miner"}
          </button>
        </div>
      </div>

      {analytics?.needs_today && (
        (analytics.needs_today.replied > 0 || analytics.needs_today.approved_due > 0 || analytics.needs_today.hot_leads > 0) && (
          <div
            className="rounded-sm border border-brand/30 bg-brand/5 p-4 flex flex-wrap items-center gap-4"
            data-testid="needs-today-strip"
          >
            <span className="text-xs uppercase tracking-wider text-brand font-medium">Needs you today</span>
            {analytics.needs_today.replied > 0 && (
              <button
                type="button"
                data-testid="needs-replied-btn"
                onClick={() => { setStatus("replied"); setActionableOnly(false); }}
                className="inline-flex items-center gap-1.5 text-sm text-white hover:text-brand"
              >
                <ChatCircleText size={16} className="text-brand" />
                {analytics.needs_today.replied} replied — respond now
              </button>
            )}
            {analytics.needs_today.approved_due > 0 && (
              <button
                type="button"
                data-testid="needs-approved-btn"
                onClick={() => { setStatus("approved"); setActionableOnly(false); }}
                className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-brand"
              >
                <Check size={16} className="text-brand" />
                {analytics.needs_today.approved_due} approved awaiting send
              </button>
            )}
            {analytics.needs_today.hot_leads > 0 && (
              <a
                href="/dashboard/leads"
                data-testid="needs-hot-leads-link"
                className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-brand"
              >
                <Fire size={16} className="text-brand" />
                {analytics.needs_today.hot_leads} hot leads in CRM
              </a>
            )}
          </div>
        )
      )}

      {settings && (
        <div
          className="rounded-sm border border-white/5 bg-[#101116] p-4 grid gap-4 md:grid-cols-4"
          data-testid="prospects-settings"
        >
          <label className="text-xs text-white/50 space-y-1.5">
            <span className="uppercase tracking-wider">Daily cap</span>
            <input
              type="number"
              min={1}
              max={200}
              data-testid="settings-daily-cap"
              defaultValue={settings.daily_cap}
              onBlur={(e) => {
                const v = Number(e.target.value);
                if (v && v !== settings.daily_cap) saveSettings({ daily_cap: v });
              }}
              className="w-full bg-[#0d0e13] border border-white/10 rounded-sm px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="text-xs text-white/50 space-y-1.5">
            <span className="uppercase tracking-wider">Min score</span>
            <input
              type="number"
              min={0}
              max={100}
              data-testid="settings-min-score"
              defaultValue={settings.min_score}
              onBlur={(e) => {
                const v = Number(e.target.value);
                if (!Number.isNaN(v) && v !== settings.min_score) saveSettings({ min_score: v });
              }}
              className="w-full bg-[#0d0e13] border border-white/10 rounded-sm px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="text-xs text-white/50 space-y-1.5 flex flex-col justify-end">
            <span className="uppercase tracking-wider mb-1.5">Require approval</span>
            <button
              type="button"
              data-testid="settings-require-approval"
              disabled={savingSettings}
              onClick={() => saveSettings({ require_approval: !settings.require_approval })}
              className={`px-3 py-2 rounded-sm border text-sm ${
                settings.require_approval
                  ? "border-brand text-brand bg-brand/10"
                  : "border-white/10 text-white/50"
              }`}
            >
              {settings.require_approval ? "On — approve first" : "Off — auto above min score"}
            </button>
          </label>
          <div className="text-xs text-white/40 space-y-1 self-end pb-1">
            <p>Sent today: <span className="text-white">{settings.sent_today ?? 0}</span> / {settings.daily_cap}</p>
            <p>Email: {settings.email_configured ? "configured" : "not configured"}</p>
            <p>
              Enrich:{" "}
              {settings.skiptrace_configured
                ? `${settings.skiptrace_provider || "on"} — ${settings.skiptrace_remaining ?? 0} of ${settings.skiptrace_cap ?? "?"} left`
                : "not configured (TRACERFY_API_KEY)"}
            </p>
            <p>{settings.approval_required_now ? "Approve gate active" : "Auto-send eligible"}</p>
            <p className="text-white/30 pt-1">
              Email-only outreach (no cold SMS). Enrich appends owner emails.
              {settings.require_approval
                ? " Turn approval Off to auto-send inbound + distress above min score (cold owners always need approval)."
                : " Auto-send: inbound + distress only — cold owner records still need your approval."}
            </p>
          </div>
        </div>
      )}

      {analytics?.classes && Object.keys(analytics.classes).length > 0 && (
        <div className="rounded-sm border border-white/5 bg-[#101116] p-4" data-testid="prospects-analytics">
          <p className="text-[11px] uppercase tracking-wider text-white/35 mb-3">
            Pipeline by signal class
            {analytics.delivery_30d && (
              <span className="normal-case tracking-normal text-white/25 ml-3">
                Last 30d: {analytics.delivery_30d.sent || 0} sent · {analytics.delivery_30d.delivered || 0} delivered
                · {analytics.delivery_30d.bounced || 0} bounced · {analytics.delivery_30d.complained || 0} spam
              </span>
            )}
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            {["inbound", "distress", "cold"].map((cls) => {
              const c = analytics.classes[cls];
              if (!c) return null;
              return (
                <div key={cls} className="rounded-sm border border-white/5 bg-[#0d0e13] p-3" data-testid={`analytics-class-${cls}`}>
                  <p className="text-xs uppercase tracking-wider text-brand mb-2">{CLASS_LABELS[cls] || cls}</p>
                  <div className="grid grid-cols-3 gap-1 text-center">
                    <div>
                      <p className="text-white text-lg">{c.total}</p>
                      <p className="text-[10px] text-white/35 uppercase">Total</p>
                    </div>
                    <div>
                      <p className="text-white text-lg">{c.sent}</p>
                      <p className="text-[10px] text-white/35 uppercase">Sent</p>
                    </div>
                    <div>
                      <p className={`text-lg ${c.replied + c.converted > 0 ? "text-brand" : "text-white"}`}>
                        {c.replied + c.converted}
                      </p>
                      <p className="text-[10px] text-white/35 uppercase">Replied+Won</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-white/30 mt-2">
                    {c.email_ready} email-ready · {c.suppressed} suppressed
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`filter-status-${s}`}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider border transition-colors ${
                status === s ? "border-brand text-brand bg-brand/10" : "border-white/10 text-white/50 hover:text-white"
              }`}
            >
              {s}
            </button>
          ))}
          <span className="w-px bg-white/10 mx-1" />
          {TYPE_FILTERS.map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`filter-ptype-${t}`}
              onClick={() => setType(t)}
              className={`px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider border transition-colors ${
                type === t ? "border-brand text-brand bg-brand/10" : "border-white/10 text-white/50 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
          <span className="w-px bg-white/10 mx-1" />
          <button
            type="button"
            data-testid="filter-actionable"
            onClick={() => setActionableOnly((v) => !v)}
            className={`px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider border transition-colors ${
              actionableOnly ? "border-brand text-brand bg-brand/10" : "border-white/10 text-white/50 hover:text-white"
            }`}
          >
            {actionableOnly ? "Email-ready" : "Include no-email"}
          </button>
        </div>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
          <input
            data-testid="prospects-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, address, email…"
            className="bg-[#101116] border border-white/10 rounded-sm pl-9 pr-3 py-2 text-sm text-white w-full sm:w-64 focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      {status === "all" && (
        <p className="text-[11px] text-white/35" data-testid="prospects-all-hint">
          “All” hides suppressed/skipped. Open those tabs or use Clear denied to remove them.
        </p>
      )}

      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="h-8 w-8 rounded-full border-2 border-brand border-t-transparent animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-white/40 space-y-2" data-testid="prospects-empty">
          <p>No prospects in this view.</p>
          {actionableOnly ? (
            <p className="text-sm text-amber-200/70 max-w-md mx-auto">
              {settings?.skiptrace_configured
                ? "Email-ready is on — no contacts with email yet. Run miner or Enrich top prospects (score ≥ enrich min)."
                : "Email-ready is on — add TRACERFY_API_KEY on Render (whyteglove-api), buy a small credit pack, then Enrich top prospects. Or click “Include no-email” to see mined owners."}
            </p>
          ) : (
            <p className="text-sm">Run the miner, or check another status filter.</p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-sm border border-white/5" data-testid="prospects-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-white/35 border-b border-white/5">
                <th className="p-3">Score</th>
                <th className="p-3">Prospect</th>
                <th className="p-3">Signal</th>
                <th className="p-3">Status</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`prospect-row-${p.id}`}>
                  <td className="p-3 align-top"><ScorePill score={p.score} /></td>
                  <td className="p-3 align-top min-w-[220px]">
                    <p className="text-white">{p.name || p.owner_name || "—"}</p>
                    <p className="text-white/45 text-xs mt-0.5 line-clamp-2">{p.address || p.region}</p>
                    <p className="text-white/30 text-[11px] mt-1">
                      {p.email || <span className="text-amber-200/70">No email — needs contact</span>}
                    </p>
                  </td>
                  <td className="p-3 align-top">
                    <p className="text-white/70 text-xs uppercase tracking-wider">{p.prospect_type}</p>
                    <p className="text-white/40 text-xs mt-1">{(p.signal_type || "").replace(/_/g, " ")}</p>
                    {Array.isArray(p.signals) && p.signals.length > 1 && (
                      <p className="text-white/25 text-[10px] mt-1">{p.signals.slice(0, 3).join(" · ")}</p>
                    )}
                  </td>
                  <td className="p-3 align-top">
                    <span className="text-xs uppercase tracking-wider text-white/50">{p.status}</span>
                    <p className="text-white/30 text-[10px] mt-1">
                      Seq step {Number(p.sequence_step) || 0}/3
                    </p>
                  </td>
                  <td className="p-3 align-top">
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        data-testid={`prospect-preview-${p.id}`}
                        onClick={() => openDraft(p)}
                        className="px-2 py-1 rounded-sm border border-white/10 text-[11px] text-white/60 hover:text-brand hover:border-brand/40"
                      >
                        <Sparkle size={12} className="inline mr-1" /> Draft
                      </button>
                      {p.status === "new" && p.email && (
                        <button
                          type="button"
                          data-testid={`prospect-approve-${p.id}`}
                          onClick={() => act(p.id, "approve")}
                          className="px-2 py-1 rounded-sm border border-brand/40 text-[11px] text-brand hover:bg-brand/10"
                        >
                          <Check size={12} className="inline mr-1" /> Approve
                        </button>
                      )}
                      {p.status === "approved" && p.email && (
                        <button
                          type="button"
                          data-testid={`prospect-send-${p.id}`}
                          onClick={() => sendNow(p.id)}
                          disabled={busy || !settings?.email_configured}
                          className="px-2 py-1 rounded-sm border border-brand/40 text-[11px] text-brand hover:bg-brand/10 disabled:opacity-60"
                        >
                          Send now
                        </button>
                      )}
                      {p.status === "sent" && (
                        <button
                          type="button"
                          data-testid={`prospect-replied-${p.id}`}
                          onClick={() => act(p.id, "replied")}
                          className="px-2 py-1 rounded-sm border border-brand/40 text-[11px] text-brand hover:bg-brand/10"
                        >
                          <ChatCircleText size={12} className="inline mr-1" /> Replied
                        </button>
                      )}
                      {p.status === "replied" && (
                        <button
                          type="button"
                          data-testid={`prospect-unreplied-${p.id}`}
                          onClick={() => act(p.id, "unreplied")}
                          className="px-2 py-1 rounded-sm border border-white/10 text-[11px] text-white/50 hover:text-white"
                        >
                          <ArrowClockwise size={12} className="inline mr-1" /> Undo replied
                        </button>
                      )}
                      {["sent", "replied"].includes(p.status) && (
                        <button
                          type="button"
                          data-testid={`prospect-convert-${p.id}`}
                          onClick={() => convertProspect(p.id)}
                          className="px-2 py-1 rounded-sm border border-brand/40 text-[11px] text-brand hover:bg-brand/10"
                        >
                          <UserPlus size={12} className="inline mr-1" /> Convert
                        </button>
                      )}
                      {["new", "approved"].includes(p.status) && (
                        <button
                          type="button"
                          data-testid={`prospect-skip-${p.id}`}
                          onClick={() => act(p.id, "skip")}
                          className="px-2 py-1 rounded-sm border border-white/10 text-[11px] text-white/50 hover:text-white"
                        >
                          <X size={12} className="inline mr-1" /> Skip
                        </button>
                      )}
                      {!["suppressed", "converted", "skipped"].includes(p.status) && (
                        <button
                          type="button"
                          data-testid={`prospect-suppress-${p.id}`}
                          onClick={() => act(p.id, "suppress")}
                          className="px-2 py-1 rounded-sm border border-white/10 text-[11px] text-white/40 hover:text-red-300"
                        >
                          <Prohibit size={12} className="inline mr-1" /> Suppress
                        </button>
                      )}
                      {["suppressed", "skipped"].includes(p.status) && (
                        <button
                          type="button"
                          data-testid={`prospect-delete-${p.id}`}
                          onClick={() => removeOne(p.id)}
                          className="px-2 py-1 rounded-sm border border-red-400/30 text-[11px] text-red-300/80 hover:bg-red-500/10"
                        >
                          <Trash size={12} className="inline mr-1" /> Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          data-testid="prospect-draft-modal"
          onClick={() => setPreview(null)}
        >
          <div
            className="max-w-lg w-full bg-[#101116] border border-white/10 rounded-sm p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-white/35">Draft preview</p>
                <h2 className="text-xl text-white mt-1">{preview.draft_subject || "No subject"}</h2>
              </div>
              <button type="button" onClick={() => setPreview(null)} className="text-white/40 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-white/70 font-sans leading-relaxed max-h-80 overflow-y-auto">
              {preview.draft_body || "No draft yet."}
            </pre>
            {preview.score_reasons?.length > 0 && (
              <ul className="text-xs text-white/40 list-disc pl-4 space-y-1">
                {preview.score_reasons.map((r) => <li key={r}>{r}</li>)}
              </ul>
            )}
            <div className="flex flex-wrap gap-2 pt-2">
              {preview.status === "new" && preview.email && (
                <button
                  type="button"
                  data-testid="modal-approve"
                  onClick={async () => { await act(preview.id, "approve"); setPreview(null); }}
                  className="px-4 py-2 rounded-sm bg-brand text-black text-xs uppercase tracking-wider"
                >
                  Approve to send
                </button>
              )}
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="px-4 py-2 rounded-sm border border-white/15 text-xs text-white/60"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
